import { computed, inject, Service, signal } from '@angular/core';
import { AuthService } from '@authentication';
import { defer, Observable, ReplaySubject, timer } from 'rxjs';
import { finalize, repeat, retry, share, tap } from 'rxjs/operators';
import {
	SSE_ACK_EVENT,
	SSE_BASE_RECONNECT_DELAY_MS,
	SSE_MAX_RECONNECT_DELAY_MS,
	SSE_OUTAGE_WARNING_MS,
	SseConnectionStatus,
	SseHttpError,
	SseMessage,
	SseStatus
} from './sse.model';
import { APP_NAME_HEADER, DEVICE_ID_HEADER, KALA_APP_NAME } from '../http/app-headers.interceptor';
import { DeviceIdService } from '../device/device-id.service';

@Service()
export class SseService {
	private readonly authService = inject(AuthService);
	private readonly deviceIdService = inject(DeviceIdService);

	/**
	 * Per-URL cache of shared, auto-reconnecting observables.
	 * Entries are never removed from the map because the `share` operator with
	 * `resetOnRefCountZero: true` handles re-subscription transparently.
	 */
	private readonly connections = new Map<string, Observable<SseMessage<unknown>>>();

	/** Status of every stream that currently has a subscriber, keyed by URL. */
	private readonly statuses = signal<ReadonlyMap<string, SseConnectionStatus>>(new Map());

	/**
	 * URLs that have failed and have not completed a handshake since — the streams currently in
	 * an outage.
	 *
	 * A stream that is down is reported on by its outage timers rather than by each failed
	 * attempt, so this is also what keeps the retry loop from restarting that reporting.
	 */
	private readonly failed = new Set<string>();

	/** Pending escalation for each stream in an outage, cancelled on recovery or teardown. */
	private readonly outageTimers = new Map<string, ReturnType<typeof setTimeout>>();

	/**
	 * Bumped every time a stream that had failed handshakes again — the one signal that means
	 * the app genuinely recovered, rather than merely stopped reporting a loss.
	 *
	 * A stream that is torn down never reaches this, which is what makes it impossible for an
	 * abandoned broken stream to be announced as a connection restored.
	 */
	readonly recoveries = signal(0);

	/**
	 * The live streams' combined state, for the connection bar.
	 *
	 * A loss outranks everything, and a stream on its way back outranks a healthy one: one feed
	 * not updating is worth saying so, whichever part of the app is carrying it. A stream still
	 * on its first attempt is outranked by both, so opening a page that starts a stream of its
	 * own does not raise the bar over prices that are in fact arriving.
	 */
	readonly status = computed<SseStatus>(() => {
		const states = [...this.statuses().values()];

		if (states.includes('disconnected')) return 'disconnected';
		if (states.includes('reconnecting')) return 'reconnecting';
		if (states.includes('connected')) return 'connected';
		return states.length > 0 ? 'connecting' : 'idle';
	});

	/**
	 * Opens (or reuses) a multiplexed SSE stream for the given `url`.
	 *
	 * @param url Full URL of the SSE endpoint.
	 * @returns Hot observable that emits typed SSE messages. Completes only when
	 *          the observable chain is deliberately unsubscribed by every subscriber.
	 */
	connect<T = unknown>(url: string): Observable<SseMessage<T>> {
		if (!this.connections.has(url)) {
			const stream$ = defer(() => {
				this.markOpening(url);
				return this.openFetchStream<T>(url);
			}).pipe(
				tap({
					next: message => {
						// Every connection opens with a handshake. It is the only proof that the
						// server is actually feeding this stream — the fetch resolving only means
						// the request was accepted.

						if (message.type === SSE_ACK_EVENT) {
							this.markHandshake(url);
						}
					},
					// Down either way — an error for `retry`, a clean server close for the
					// `repeat` below. Both are the same loss, and both wait before trying
					// again, so the outage is reported rather than the attempt after it.
					error: () => this.markLost(url),
					complete: () => this.markLost(url)
				}),
				retry({
					delay: (_error, retryCount) =>
						timer(
							Math.min(SSE_BASE_RECONNECT_DELAY_MS * 2 ** (retryCount - 1), SSE_MAX_RECONNECT_DELAY_MS)
						),
					resetOnSuccess: true
				}),
				// `retry` only resubscribes on errors. A server ending the stream cleanly would
				// otherwise leave its subscribers — the header clock, the market feed — without a
				// stream for the rest of the session, waiting on a page change to bring one back.
				repeat({ delay: () => timer(SSE_BASE_RECONNECT_DELAY_MS) }),
				// Last subscriber out: the stream is torn down, so it stops being one of the
				// live streams this service reports on.
				finalize(() => this.forget(url)),
				share({
					connector: () => new ReplaySubject<SseMessage<T>>(1),
					resetOnRefCountZero: true,
					resetOnError: false,
					resetOnComplete: true
				})
			);

			this.connections.set(url, stream$ as Observable<SseMessage<unknown>>);
		}

		return this.connections.get(url) as Observable<SseMessage<T>>;
	}

	// ---------------------------------------------------------------------------
	// Connection status
	// ---------------------------------------------------------------------------

	/**
	 * Records that a stream is being opened — for a stream nothing is known about yet, whose
	 * first attempt is not news. One in an outage is already being reported on by its own
	 * timers, and an attempt starting is not news either.
	 */
	private markOpening(url: string): void {
		if (this.failed.has(url)) return;
		this.setStatus(url, 'connecting');
	}

	private markHandshake(url: string): void {
		this.clearOutageTimers(url);

		if (this.failed.delete(url)) {
			this.recoveries.update(count => count + 1);
		}

		this.setStatus(url, 'connected');
	}

	private markLost(url: string): void {
		// Only the first failure of an outage is news. The ones after it are the retry loop
		// doing its job, and are owned by the timer below rather than reported one by one.
		if (this.failed.has(url)) return;

		this.failed.add(url);

		// Straight to `reconnecting` while the loop works, and it stays there for the whole
		// window: the drop is not announced as a loss while the stream still has a chance of
		// coming back within it. A handshake at any point clears the timer and goes green.
		this.setStatus(url, 'reconnecting');
		this.setOutageTimer(url, SSE_OUTAGE_WARNING_MS, () => this.setStatus(url, 'disconnected'));
	}

	private setOutageTimer(url: string, delayMs: number, onElapsed: () => void): void {
		this.outageTimers.set(
			url,
			setTimeout(() => {
				this.outageTimers.delete(url);
				onElapsed();
			}, delayMs)
		);
	}

	private clearOutageTimers(url: string): void {
		const timer = this.outageTimers.get(url);

		if (timer === undefined) return;

		clearTimeout(timer);
		this.outageTimers.delete(url);
	}

	private setStatus(url: string, status: SseConnectionStatus): void {
		this.statuses.update(statuses => new Map(statuses).set(url, status));
	}

	/** Drops what is remembered about a stream that no longer has a subscriber. */
	private forget(url: string): void {
		this.clearOutageTimers(url);
		this.failed.delete(url);

		if (!this.statuses().has(url)) {
			return;
		}

		this.statuses.update(statuses => {
			const live = new Map(statuses);
			live.delete(url);
			return live;
		});
	}

	// ---------------------------------------------------------------------------
	// Internal helpers
	// ---------------------------------------------------------------------------

	/**
	 * Creates a cold Observable that opens a single `fetch()` SSE connection and
	 * emits each parsed SSE message. The request is aborted when the subscriber
	 * unsubscribes. The observable errors when the server responds with a non-2xx
	 * status or when the network drops, which triggers the `retry` operator above.
	 */
	private openFetchStream<T>(url: string): Observable<SseMessage<T>> {
		return new Observable<SseMessage<T>>(subscriber => {
			const controller = new AbortController();
			const token = this.authService.getToken();

			// The stream is opened with `fetch`, so it never passes through
			// `AppHeadersInterceptor` — it has to identify the app and device itself.
			const deviceId = this.deviceIdService.deviceId();

			const headers: Record<string, string> = {
				Accept: 'text/event-stream',
				'Cache-Control': 'no-cache',
				[APP_NAME_HEADER]: KALA_APP_NAME
			};

			if (deviceId) {
				headers[DEVICE_ID_HEADER] = deviceId;
			}

			if (token) {
				headers['Authorization'] = `Bearer ${token}`;
			}

			fetch(url, { headers, signal: controller.signal })
				.then(response => {
					if (!response.ok) {
						subscriber.error(new SseHttpError(response.status, url));
						return;
					}

					if (!response.body) {
						subscriber.error(new Error(`SSE: no response body received from ${url}`));
						return;
					}

					const reader = response.body.getReader();
					const decoder = new TextDecoder();
					let buffer = '';

					const readChunk = (): void => {
						reader
							.read()
							.then(({ done, value }) => {
								if (done) {
									// Server closed the stream — signal completion so `retry` can reconnect.
									subscriber.complete();
									return;
								}

								buffer += decoder.decode(value, { stream: true });
								const { messages, remaining } = parseSseBuffer<T>(buffer);
								buffer = remaining;
								messages.forEach(msg => subscriber.next(msg));
								readChunk();
							})
							.catch((error: unknown) => {
								if ((error as Error)?.name !== 'AbortError') {
									subscriber.error(error);
								}
							});
					};

					readChunk();
				})
				.catch((error: unknown) => {
					if ((error as Error)?.name !== 'AbortError') {
						subscriber.error(error);
					}
				});

			// Teardown: abort the in-flight request when the subscriber leaves.
			return () => controller.abort();
		});
	}
}

// ---------------------------------------------------------------------------
// SSE text-stream parser
// ---------------------------------------------------------------------------

/**
 * Parses as many complete SSE event blocks as possible from `buffer`.
 * Events are delimited by a blank line (`\n\n`). Any incomplete trailing
 * content is returned as `remaining` to be prepended to the next chunk.
 */
function parseSseBuffer<T>(buffer: string): { messages: SseMessage<T>[]; remaining: string } {
	const messages: SseMessage<T>[] = [];

	// Split on the double-newline event separator.
	const blocks = buffer.split('\n\n');

	// The last element is either empty (buffer ended on \n\n) or an incomplete
	// block that must be preserved for the next call.
	const remaining = blocks.pop() ?? '';

	for (const block of blocks) {
		if (!block.trim()) continue;

		let eventType = 'message';
		const dataLines: string[] = [];
		let id: string | undefined;
		let retryMs: number | undefined;

		for (const line of block.split('\n')) {
			if (line.startsWith('event:')) {
				eventType = line.slice(6).trim();
			} else if (line.startsWith('data:')) {
				dataLines.push(line.slice(5).trim());
			} else if (line.startsWith('id:')) {
				id = line.slice(3).trim();
			} else if (line.startsWith('retry:')) {
				const parsed = Number(line.slice(6).trim());
				if (!Number.isNaN(parsed)) retryMs = parsed;
			}
			// Comment lines (starting with ':') are intentionally ignored.
		}

		if (dataLines.length === 0) continue;

		try {
			const data = JSON.parse(dataLines.join('\n')) as T;
			messages.push({ type: eventType, data, id, retryMs });
		} catch {
			// Skip events whose data field is not valid JSON.
		}
	}

	return { messages, remaining };
}
